import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  ChevronDown,
  Menu,
  Bell,
  Plus
} from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useNotifications } from "@/hooks/use-notifications";
import { useSystemSettings } from "@/hooks/use-system-settings";
import { useCommunitySettings } from "@/hooks/use-community";
import { cn } from "@/lib/utils";
import { APP_NAME } from "@/lib/constants";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Navbar = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState<any>(null);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);

  const { data: settings } = useSystemSettings();
  const { data: communitySettings } = useCommunitySettings();
  
  const showAddSystem = settings?.nav_show_add_system !== 'false';
  const showCommunityNav = settings?.nav_show_community_feed !== 'false';
  const isCommunityEnabled = communitySettings?.enabled !== false;

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const { data: profile } = useQuery({
    queryKey: ['profile', session?.user?.id],
    queryFn: async (): Promise<any> => {
      if (!session?.user?.id) return null;
      const { data } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
      return data;
    },
    enabled: !!session?.user?.id,
  });

  const { data: navItems } = useQuery({
    queryKey: ['navigation-items-public'],
    queryFn: async () => {
      const { data } = await supabase
        .from('navigation_items')
        .select('*')
        .eq('is_active', true)
        .order('order_index', { ascending: true });
      return data || [];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications(session?.user?.id);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const isAdmin = (profile as any)?.role === 'admin' || (profile as any)?.role === 'super_admin';

  const AiToolsDropdown = ({ mobile = false }: { mobile?: boolean }) => {
    if (mobile) {
      return (
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="ai-tools" className="border-none">
            <AccordionTrigger className="text-2xl font-bold text-slate-900 hover:text-blue-600 transition-colors py-0 hover:no-underline tracking-tight">
              כלי AI
            </AccordionTrigger>
            <AccordionContent className="pb-0 pt-6 pr-4">
              <div className="flex flex-col gap-6">
                <SheetClose asChild>
                  <Link to="/ai-models" className="text-lg font-medium text-slate-500 hover:text-blue-600 block">מודלי AI</Link>
                </SheetClose>
                <div className="h-px bg-slate-100/50 w-full" />
                <SheetClose asChild>
                  <Link to="/ai-dev-tools" className="text-lg font-medium text-slate-500 hover:text-blue-600 block">בניית מערכות</Link>
                </SheetClose>
                <div className="h-px bg-slate-100/50 w-full" />
                <SheetClose asChild>
                  <Link to="/ai-media-tools" className="text-lg font-medium text-slate-500 hover:text-blue-600 block">גרפיקה וסרטונים</Link>
                </SheetClose>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      );
    }

    return (
      <DropdownMenu open={aiMenuOpen} onOpenChange={setAiMenuOpen} modal={false} dir="rtl">
        <DropdownMenuTrigger asChild>
          <div 
            className="relative py-4 px-2 cursor-pointer outline-none"
            onMouseEnter={() => setAiMenuOpen(true)}
            onMouseLeave={() => setAiMenuOpen(false)}
          >
            <button className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors group">
              כלי AI
              <ChevronDown className={cn("h-3 w-3 transition-transform duration-300 text-slate-400 group-hover:text-slate-600", aiMenuOpen && "rotate-180")} />
            </button>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="center"
          side="bottom"
          sideOffset={5}
          className="w-56 rounded-xl p-1.5 shadow-lg border-slate-100 z-[60] bg-white/95 backdrop-blur-sm animate-in fade-in zoom-in-95 duration-200" 
          onMouseEnter={() => setAiMenuOpen(true)}
          onMouseLeave={() => setAiMenuOpen(false)}
        >
          <DropdownMenuItem onClick={() => navigate('/ai-models')} className="rounded-lg cursor-pointer py-2.5 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:bg-slate-50 transition-colors">
            מודלי AI
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/ai-dev-tools')} className="rounded-lg cursor-pointer py-2.5 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:bg-slate-50 transition-colors">
            כלי AI לבניית מערכות
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/ai-media-tools')} className="rounded-lg cursor-pointer py-2.5 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:bg-slate-50 transition-colors">
            כלי AI גרפיקה/סרטונים
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <nav className="fixed top-0 start-0 end-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200 pt-safe">
      <div className="container mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        
        {/* Logo & Desktop Links */}
        <div className="flex items-center gap-6 md:gap-8">
          <Link to="/" className="text-lg font-bold text-slate-900 tracking-tight">
            {APP_NAME}
          </Link>
          
          <div className="hidden md:flex items-center gap-6">
            {navItems?.map((item: any) => {
              if (item.path === 'SPECIAL:AI_TOOLS') {
                return <AiToolsDropdown key={item.id} />;
              }
              const isExternal = item.path.startsWith('http');
              if (isExternal) {
                return (
                  <a 
                    key={item.id} 
                    href={item.path} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm font-medium text-slate-600 hover:text-slate-900"
                  >
                    {item.label}
                  </a>
                );
              }
              return (
                <Link 
                  key={item.id} 
                  to={item.path} 
                  className="text-sm font-medium text-slate-600 hover:text-slate-900"
                >
                  {item.label}
                </Link>
              );
            })}
            
            {/* Built-in Community Feed Link */}
            {isCommunityEnabled && showCommunityNav && (
              <Link 
                to="/community" 
                className="text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-1.5"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
                פיד הקהילה
              </Link>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 md:gap-4">
          
          {/* Notifications */}
          {session && (
            <DropdownMenu modal={false} dir="rtl">
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative text-slate-500 h-9 w-9">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-2 end-2 h-2 w-2 bg-red-600 rounded-full ring-2 ring-white" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[300px] md:w-80 rounded-xl border-slate-200 shadow-lg p-0 mt-2" forceMount>
                <div className="p-3 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 rounded-t-xl">
                  <h4 className="font-semibold text-slate-900 text-sm">התראות</h4>
                  {unreadCount > 0 && (
                    <button onClick={() => markAllAsRead()} className="text-xs text-blue-600 font-medium">סמן הכל כנקרא</button>
                  )}
                </div>
                <div className="max-h-[350px] overflow-y-auto py-1">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-slate-500 text-sm">אין התראות חדשות</div>
                  ) : (
                    notifications.map((n: any) => (
                      <div key={n.id} className={cn("px-4 py-3 hover:bg-slate-50 border-b border-slate-50 last:border-0 cursor-pointer text-right", !n.is_read && "bg-blue-50/30")} onClick={() => { 
                        if (!n.is_read) markAsRead(n.id); 
                        if (n.link) {
                           if (n.link.startsWith('http')) {
                             window.open(n.link, '_blank');
                           } else {
                             navigate(n.link);
                           }
                        }
                      }}>
                        <p className={cn("text-sm font-medium mb-1", !n.is_read ? "text-blue-900" : "text-slate-900")}>{n.title}</p>
                        <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{n.message}</p>
                      </div>
                    ))
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Desktop Add System Button */}
          {showAddSystem && (
            <Link to="/dashboard/create" className="hidden md:flex">
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5 rounded-xl h-9 md:h-10 px-4 font-bold shadow-none transition-transform hover:scale-105">
                <Plus className="w-4 h-4" />
                הוספת מערכת
              </Button>
            </Link>
          )}

          {/* Desktop Profile / Auth Buttons */}
          {session ? (
            <div className="hidden md:flex items-center gap-2">
              <DropdownMenu modal={false} dir="rtl">
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-9 w-9 md:h-10 md:w-10 rounded-full p-0 border border-slate-200">
                    <Avatar className="h-full w-full">
                      <AvatarFallback className="bg-slate-100 text-slate-600 font-medium text-xs">
                        {(profile as any)?.display_name?.[0]?.toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 p-1 rounded-xl border-slate-200 shadow-lg mt-2" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal p-3">
                    <div className="flex flex-col space-y-1 text-start">
                      <p className="text-sm font-medium leading-none text-slate-900">{(profile as any)?.display_name || "משתמש"}</p>
                      <p className="text-xs leading-none text-slate-500 font-normal truncate" dir="ltr" style={{ textAlign: 'right' }}>{session.user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-100" />
                  <DropdownMenuItem onClick={() => navigate('/account')} className="rounded-lg cursor-pointer">החשבון שלי</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} className="rounded-lg cursor-pointer">ניהול מערכות</DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator className="bg-slate-100" />
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="rounded-lg cursor-pointer font-semibold text-blue-700">ניהול אתר</DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-slate-100" />
                  <DropdownMenuItem onClick={handleSignOut} className="text-red-600 focus:text-red-700 focus:bg-red-50 rounded-lg cursor-pointer">התנתק</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/login"><Button variant="ghost" className="text-slate-600 text-sm">התחבר</Button></Link>
              <Link to="/register"><Button className="bg-slate-900 text-white rounded-lg text-sm">הרשמה</Button></Link>
            </div>
          )}

          {/* Mobile Navigation Menu */}
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-600">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent 
                side="right" 
                className="w-[85%] sm:w-[380px] p-0 bg-white/95 backdrop-blur-xl border-l border-slate-200 transition-all duration-500 ease-in-out [&>button]:hidden overflow-y-auto" 
                dir="rtl"
              >
                <SheetHeader className="sr-only">
                  <SheetTitle>תפריט ניווט</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col min-h-full pt-20 px-8 pb-10">
                  <nav className="flex flex-col">
                    {isCommunityEnabled && showCommunityNav && (
                      <div className="border-b border-slate-200/60 pb-5 mb-5">
                        <SheetClose asChild>
                          <Link to="/community" className="text-2xl font-bold text-blue-600 hover:text-blue-700 transition-colors tracking-tight block">
                            פיד הקהילה
                          </Link>
                        </SheetClose>
                      </div>
                    )}
                    
                    {navItems?.map((item: any) => (
                      <div key={item.id} className="border-b border-slate-200/60 pb-5 mb-5 last:border-0">
                        {item.path === 'SPECIAL:AI_TOOLS' ? (
                          <AiToolsDropdown mobile={true} />
                        ) : item.path.startsWith('http') ? (
                          <SheetClose asChild>
                            <a href={item.path} target="_blank" rel="noopener noreferrer" className="text-2xl font-bold text-slate-900 hover:text-blue-600 transition-colors tracking-tight block">
                              {item.label}
                            </a>
                          </SheetClose>
                        ) : (
                          <SheetClose asChild>
                            <Link to={item.path} className="text-2xl font-bold text-slate-900 hover:text-blue-600 transition-colors tracking-tight block">
                              {item.label}
                            </Link>
                          </SheetClose>
                        )}
                      </div>
                    ))}
                    
                    {/* Mobile Add System Button */}
                    {showAddSystem && (
                      <div className="border-b border-slate-200/60 pb-5 mb-5">
                        <SheetClose asChild>
                          <Link to="/dashboard/create" className="text-2xl font-bold text-blue-600 hover:text-blue-700 transition-colors tracking-tight flex items-center gap-2">
                            <Plus className="h-6 w-6" />
                            הוספת מערכת
                          </Link>
                        </SheetClose>
                      </div>
                    )}

                    {isAdmin && (
                      <div className="border-b border-slate-200/60 pb-5 mb-5">
                        <SheetClose asChild>
                          <Link to="/admin" className="text-2xl font-bold text-blue-700 hover:text-blue-800 transition-colors tracking-tight block">
                            ניהול אתר
                          </Link>
                        </SheetClose>
                      </div>
                    )}
                  </nav>

                  {!session ? (
                    <div className="mt-8 flex flex-col gap-4">
                      <SheetClose asChild>
                        <Link to="/login" className="w-full">
                          <Button variant="outline" className="w-full rounded-2xl h-14 text-lg border-slate-200 font-bold">התחברות</Button>
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/register" className="w-full">
                          <Button className="w-full rounded-2xl h-14 text-lg bg-slate-900 font-bold">הרשמה בחינם</Button>
                        </Link>
                      </SheetClose>
                    </div>
                  ) : (
                    <div className="mt-8 space-y-6">
                      <div className="flex flex-col gap-4">
                        <div className="border-b border-slate-200/60 pb-5">
                          <SheetClose asChild>
                             <Link to="/account" className="text-lg font-bold text-slate-900 block">החשבון שלי</Link>
                          </SheetClose>
                        </div>
                        <div className="border-b border-slate-200/60 pb-5">
                          <SheetClose asChild>
                             <Link to="/dashboard" className="text-lg font-bold text-slate-900 block">ניהול מערכות</Link>
                          </SheetClose>
                        </div>
                      </div>
                      <Button onClick={handleSignOut} variant="ghost" className="text-red-600 text-lg font-bold p-0 h-auto hover:bg-transparent justify-start w-full">התנתק</Button>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>

        </div>
      </div>
    </nav>
  );
};

export default Navbar;